'use client';

import { useCallback } from 'react';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { useWebstoreSession } from '@/app/[locale]/(order)/components/auth/webstore-session-provider';
import { useCartStore } from '@/store/cart/cart-store-provider';
import type { MenuItem } from '@/types/item';

interface UseStoreContextValidationProps {
  item: MenuItem | null;
  pageId?: string;
  slug?: string;
  branchId?: string;
}

export function useStoreContextValidation({
  item,
  pageId,
  slug,
  branchId,
}: UseStoreContextValidationProps) {
  const { session, mode, storeData } = useWebstoreSession();
  const currentBranchId = useCartStore((state) => state.branchId);
  const clearCartIfDifferentStore = useCartStore(
    (state) => state.clearCartIfDifferentStore
  );
  const setBranchId = useCartStore((state) => state.setBranchId);
  const goToOrderWebstore = useGoToOrderWebstore();

  /**
   * Kiểm tra và thiết lập store context nếu cần
   * @returns true nếu context đã được thiết lập đúng, false nếu có lỗi
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const validateAndSetStoreContext = useCallback((): boolean => {
    if (!item) {
      console.warn('Item is required for store context validation');
      return false;
    }

    // Xác định thông tin store từ các nguồn khác nhau
    let targetPageId: string | undefined;
    let targetSlug: string | undefined;
    let targetBranchId: string | undefined;

    // 1. Ưu tiên thông tin được truyền vào (từ chat bot, deal progress, etc.)
    if (pageId && slug && branchId) {
      targetPageId = pageId;
      targetSlug = slug;
      targetBranchId = branchId;
    }
    // 2. Lấy từ session hiện tại nếu đang ở webview mode
    // else if (mode === 'webview' && session) {
    //   targetPageId = session.page_id;
    //   targetBranchId = currentBranchId;
    //   // Trong webview mode, slug có thể lấy từ localStorage hoặc URL
    //   if (typeof window !== 'undefined') {
    //     // eslint-disable-next-line sonarjs/no-duplicate-string
    //     targetSlug = localStorage.getItem('ahafood.webstore_slug') ?? undefined;
    //   }
    // }
    // 3. Lấy từ localStorage nếu đang ở webstore mode
    else if (mode === 'webstore') {
      if (typeof window !== 'undefined') {
        targetPageId =
          localStorage.getItem('ahafood.webstore_page_id') ?? undefined;
        targetSlug = localStorage.getItem('ahafood.webstore_slug') ?? undefined;
      }
      targetBranchId = currentBranchId;
    }

    // Kiểm tra xem có đủ thông tin không
    if (!targetPageId || !targetBranchId) {
      console.warn('Missing required store information:', {
        targetPageId,
        targetBranchId,
        targetSlug,
      });
      return false;
    }

    try {
      // Kiểm tra và xử lý việc đổi store
      if (currentBranchId && currentBranchId !== targetBranchId) {
        console.log(
          'Detected store change, clearing cart and updating context'
        );
        clearCartIfDifferentStore();
      }

      // Cập nhật store context nếu cần
      if (currentBranchId !== targetBranchId) {
        setBranchId(targetBranchId, storeData?._id.toString() ?? '');
      }

      // Nếu có đủ thông tin cho webstore, cập nhật localStorage và context
      if (targetPageId && targetSlug) {
        goToOrderWebstore(
          targetPageId,
          targetSlug,
          targetBranchId,
          item,
          storeData?._id.toString() ?? ''
        );
      }

      return true;
    } catch (error) {
      console.error('Error during store context validation:', error);
      return false;
    }
  }, [
    item,
    pageId,
    slug,
    branchId,
    mode,
    session,
    currentBranchId,
    clearCartIfDifferentStore,
    setBranchId,
    goToOrderWebstore,
  ]);

  /**
   * Kiểm tra xem có cần validate store context không
   * @returns true nếu cần validate
   */
  const needsValidation = useCallback((): boolean => {
    if (!item) return false;

    // Luôn validate nếu có thông tin store được truyền vào (từ external sources)
    if (pageId && slug && branchId) {
      return true;
    }

    // Validate nếu chưa có branchId
    if (!currentBranchId) {
      return true;
    }

    // Validate nếu đang ở webstore mode nhưng chưa có localStorage
    if (mode === 'webstore' && typeof window !== 'undefined') {
      const storedPageId = localStorage.getItem('ahafood.webstore_page_id');
      const storedSlug = localStorage.getItem('ahafood.webstore_slug');
      if (!storedPageId || !storedSlug) {
        return true;
      }
    }

    return false;
  }, [item, pageId, slug, branchId, currentBranchId, mode]);

  return {
    validateAndSetStoreContext,
    needsValidation,
    currentBranchId,
  };
}
